import { useState, useMemo } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Actions from '../../../../../store/Actions';
import { useConfiguration } from '../../../../../store/configurationStore';
import { useSnackbar } from '../../../../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../../../../context/Snackbar/Types';
import { MenuItem } from '../../../../BaseThreeDotMenu/types';

interface UseTemplateTabsActionsProps {
  activeTemplateId: string | null;
  activeTemplateName: string | null;
}

export const useTemplateTabsActions = ({
  activeTemplateId,
  activeTemplateName,
}: UseTemplateTabsActionsProps) => {
  const { templates, updateTemplate, deleteTemplate } = useConfiguration();
  const { showSnackbar } = useSnackbar();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );
  const [selectedTemplateName, setSelectedTemplateName] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Get list of existing template names (excluding the one being edited)
  const existingTemplateNames = useMemo(() => {
    return templates.filter(t => t.id !== activeTemplateId).map(t => t.name);
  }, [templates, activeTemplateId]);

  // Create menu items for BaseThreeDotMenu
  const menuItems = useMemo<MenuItem[]>(() => {
    if (!activeTemplateId || !activeTemplateName) {
      return [];
    }

    return [
      {
        id: 'edit',
        label: 'Edit Template',
        icon: <EditIcon />,
        onClick: () => handleEditTemplate(activeTemplateId, activeTemplateName),
      },
      {
        id: 'delete',
        label: 'Delete Template',
        icon: <DeleteIcon />,
        onClick: () => handleDeleteTemplate(activeTemplateId),
        destructive: true,
      },
    ];
  }, [activeTemplateId, activeTemplateName]);

  // Open edit modal
  const handleEditTemplate = (
    templateId: string,
    templateName: string
  ): void => {
    setSelectedTemplateId(templateId);
    setSelectedTemplateName(templateName);
    setEditModalOpen(true);
  };

  // Close edit modal
  const handleCloseEditModal = (): void => {
    setEditModalOpen(false);
    setSelectedTemplateId(null);
    setSelectedTemplateName('');
  };

  // Handle edit submit
  const handleEditSubmit = async (newName: string): Promise<void> => {
    if (!selectedTemplateId) {
      console.error('⚠️ No template selected for editing');
      return;
    }

    try {
      // Call rename API
      await Actions.renameConfigurationTemplate(selectedTemplateId, newName);

      // Update store
      updateTemplate(selectedTemplateId, { name: newName });

      // Show success notification
      showSnackbar({
        title: 'Template Renamed',
        message: `Template renamed to "${newName}"`,
        type: SnackbarType.success,
      });

      // Close modal
      handleCloseEditModal();
    } catch (error) {
      console.error('Failed to rename template:', error);
      showSnackbar({
        title: 'Error',
        message: 'Failed to rename template',
        type: SnackbarType.error,
      });
      throw error; // Re-throw so modal can handle it
    }
  };

  // Open delete confirmation modal
  const handleDeleteTemplate = (templateId: string): void => {
    setSelectedTemplateId(templateId);
    setDeleteModalOpen(true);
  };

  // Close delete confirmation modal
  const handleCloseDeleteModal = (): void => {
    setDeleteModalOpen(false);
    setSelectedTemplateId(null);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async (): Promise<void> => {
    if (!selectedTemplateId) {
      console.error('⚠️ No template selected for deletion');
      return;
    }

    setIsDeleting(true);

    try {
      // Call delete API
      await Actions.deleteConfigurationTemplate(selectedTemplateId);

      // Delete from store
      deleteTemplate(selectedTemplateId);

      // Show success notification
      showSnackbar({
        title: 'Template Deleted',
        message: 'Template deleted successfully',
        type: SnackbarType.success,
      });

      // Close modal
      handleCloseDeleteModal();
    } catch (error) {
      console.error('Failed to delete template:', error);
      showSnackbar({
        title: 'Error',
        message: 'Failed to delete template',
        type: SnackbarType.error,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    // Menu items
    menuItems,

    // Edit modal state
    editModalOpen,
    handleCloseEditModal,
    handleEditSubmit,
    selectedTemplateName,
    existingTemplateNames,

    // Delete modal state
    deleteModalOpen,
    handleCloseDeleteModal,
    handleDeleteConfirm,
    isDeleting,
  };
};
